import React, { createContext, useContext, useState } from 'react';
import { API_CONFIG, getApiUrl } from '../config/api';
import { getDeviceId } from '../utils/deviceId';
import { getLotSize } from '../services/utilities/lotSize';

const ContractContext = createContext();

// Create a store object to hold the contract map and utility functions
export const contractStore = {
  contractMap: new Map(),
  updateContract: null,
  fetchScripInfo: null,
  getScripDetails: null
};

// Update the cache duration constant
const CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

export const ContractProvider = ({ children }) => {
  const [contractMap, setContractMap] = useState(new Map());

  const updateContract = (contract) => {
    const key = `${contract.exchange}_${contract.token}`;
    setContractMap(prevMap => {
      const newMap = new Map(prevMap);
      newMap.set(key, {
        ...contract,
        lastUpdated: new Date().getTime()
      });
      return newMap;
    });
  };

  const fetchScripInfo = async (exchange, token) => {
    try {
      const authToken = localStorage.getItem('auth_token');
      const response = await fetch(
        `${getApiUrl(API_CONFIG.ENDPOINTS.INSTRUMENT_INFO)}/${exchange}?info=scrip&token=${token}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization-token': authToken,
            'x-device-type': 'WEB',
            'x-device-id': getDeviceId()
          }
        }
      );

      const data = await response.json();
      if (!data.error || data.error.code === 0) {
        const scripInfo = data.result;
        // Store the scrip info in the contract map with processed data
        const processedData = {
          exchange: scripInfo.exchange,
          token: scripInfo.instrument_token,
          trading_symbol: scripInfo.trading_symbol,
          display_name: scripInfo.display_name,
          expiry: scripInfo.expiry,
          instrument_type: scripInfo.instrument_type,
          strike: scripInfo.strike,
          option_type: scripInfo.option_type,
          tick_size: scripInfo.tick_size,
          lot_size: getLotSize(scripInfo),
          multiplier: scripInfo.multiplier,
          freeze_qty: scripInfo.freeze,
          circuit: {
            upper: scripInfo.higher_circuit_limit,
            lower: scripInfo.lower_circuit_limit
          },
          span_margins: scripInfo.span,
          var_margin: scripInfo.var,
          exposure_margin: scripInfo.exposure,
          max_single_order_qty: scripInfo.max_single_qty,
          isin: scripInfo.isin,
          underlying: {
            token: scripInfo.underlying_token,
            futures_token: scripInfo.fut_underlying_token
          },
          tradable: scripInfo.tradable,
          series: scripInfo.series,
          precision: scripInfo.precision,
          mtf_eligible: scripInfo.is_mtf_eligible,
          mis_eligible: scripInfo.is_mis_eligible,
          close_price: scripInfo.close_price,
          dpr_range: scripInfo.dpr,
          company_name: scripInfo.company_name,
          instrument_name: scripInfo.instrument_name,
          settlement: {
            method: scripInfo.settlement_method,
            price: scripInfo.settlement_price
          }
        };
        
        updateContract(processedData);
        return processedData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching scrip info:', error);
      return null;
    }
  };

  // Central function to get scrip details
  const getScripDetails = async (exchange, token, forceRefresh = false) => {
    const key = `${exchange}_${token}`;
    const existingContract = contractMap.get(key);

    // Check if we have valid cached data
    if (!forceRefresh && existingContract) {
      const cacheAge = Date.now() - existingContract.lastUpdated;
      // Use cached data if it's less than 2 hours old
      if (cacheAge < CACHE_DURATION) {
        return existingContract;
      }
    }

    // Fetch fresh data if needed
    const data = await fetchScripInfo(exchange, token);
    return data;
  };

  // Store the functions in the store
  contractStore.updateContract = updateContract;
  contractStore.fetchScripInfo = fetchScripInfo;
  contractStore.getScripDetails = getScripDetails;
  // Expose the map through the store
  contractStore.contractMap = contractMap;

  return (
    <ContractContext.Provider value={{ 
      contractMap, 
      updateContract, 
      fetchScripInfo,
      getScripDetails 
    }}>
      {children}
    </ContractContext.Provider>
  );
};

// Custom hook for easy access to scrip details
export const useScripDetails = (exchange, token) => {
  const { getScripDetails } = useContract();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchDetails = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getScripDetails(exchange, token, forceRefresh);
      if (result) {
        setData(result);
      } else {
        setError('Failed to fetch scrip details');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    scripDetails: data,
    loading,
    error,
    refreshScripDetails: () => fetchDetails(true),
    fetchDetails
  };
};

export const useContract = () => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error('useContract must be used within a ContractProvider');
  }
  return context;
}; 